// frontend/src/components/AgentList.jsx
// Updated: April 7, 2025
import React, { useEffect, useState } from 'react';
import { US_STATES } from '../constants';
import { apiClient } from '../api';
import { Mail, ShoppingCart, Eye, EyeOff } from 'lucide-react';

const AgentList = ({ agents, onEdit, onDelete }) => {
  const [agentStats, setAgentStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  useEffect(() => {
    // Fetch email statistics for each agent
    const fetchAgentStats = async () => {
      if (!agents || agents.length === 0) return;
      
      setLoadingStats(true);
      setStatsError(null);
      const stats = {};
      
      // Initialize stats for all agents to avoid undefined errors
      agents.forEach(agent => {
        stats[agent.id] = {
          emailsSent: 0,
          initialEmails: 0,
          fullEmails: 0,
          purchases: 0,
          totalSpent: 0
        };
      });
      
      try {
        console.log(`[${new Date().toISOString()}] Fetching stats for ${agents.length} agents`);
        
        // Create a promise for each agent's stats
        const promises = agents.map(async (agent) => {
          try {
            // Get emails sent by this agent
            const emailResponse = await apiClient.get(`/admin/email/stats/agent/${agent.id}`);
            
            // Get purchases made by this agent
            const purchaseResponse = await apiClient.get(`/admin/transactions/agent/${agent.id}`);
            
            return {
              agentId: agent.id,
              stats: {
                emailsSent: emailResponse.data.success ? emailResponse.data.total || 0 : 0,
                initialEmails: emailResponse.data.success ? emailResponse.data.initial || 0 : 0,
                fullEmails: emailResponse.data.success ? emailResponse.data.full || 0 : 0,
                purchases: purchaseResponse.data.success ? purchaseResponse.data.count || 0 : 0,
                totalSpent: purchaseResponse.data.success ? purchaseResponse.data.total || 0 : 0
              }
            };
          } catch (err) {
            console.error(`Error fetching stats for agent ${agent.id}:`, err);
            return {
              agentId: agent.id,
              stats: {
                emailsSent: 0,
                initialEmails: 0,
                fullEmails: 0,
                purchases: 0,
                totalSpent: 0
              }
            };
          }
        });
        
        // Wait for all promises to resolve
        const results = await Promise.all(promises);
        
        // Convert results array to object keyed by agent ID
        results.forEach(result => {
          stats[result.agentId] = result.stats;
        });
        
        console.log(`[${new Date().toISOString()}] Successfully fetched stats for all agents`);
        setAgentStats(stats);
      } catch (err) {
        console.error('Error fetching agent stats:', err);
        setStatsError('Failed to load agent statistics. Using default values.');
        // Still set default stats even if fetch fails
        setAgentStats(stats);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchAgentStats();
  }, [agents]);

  const togglePasswordVisibility = (agentId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  // Ensure agents is an array and has items
  if (!Array.isArray(agents)) {
    console.error('Agents is not an array:', agents);
    return (
      <div className="no-agents text-center p-4">
        <p className="text-text-secondary">No agents found or failed to load agents.</p>
      </div>
    );
  }

  console.log('Rendering agent list with:', agents.length, 'agents');

  return (
    <div className="agent-list">
      {agents.length === 0 ? (
        <div className="no-agents text-center p-4">
          <p className="text-text-secondary">No agents found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border-primary">
            <thead className="bg-background-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Password</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Admin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">States</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider bg-status-info-bg">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Emails Sent
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider bg-status-info-bg">
                  <div className="flex items-center">
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    Purchases
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-background-secondary divide-y divide-border-primary">
              {agents.map((agent) => (
                <tr key={agent.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{agent.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{agent.name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{agent.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    <div className="flex items-center">
                      <span className="mr-2">
                        {visiblePasswords[agent.id] ? agent.password : '••••••••'}
                      </span>
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility(agent.id)}
                        className="text-text-secondary hover:text-text-primary focus:outline-none"
                      >
                        {visiblePasswords[agent.id] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {agent.isAdmin ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-success-bg text-status-success-text">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-background-tertiary text-text-secondary">
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {new Date(agent.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    <span className="text-text-tertiary">—</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary">
                    {agent.selectedStates && agent.selectedStates.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {agent.selectedStates.map((state) => (
                          <span
                            key={state}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-info-bg text-status-info-text"
                          >
                            {US_STATES[state] || state}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-text-tertiary">No states selected</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm bg-status-info-bg">
                    {loadingStats ? (
                      <span className="text-text-tertiary">Loading...</span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="font-medium">{agentStats[agent.id]?.emailsSent || 0} total</span>
                        <div className="text-xs text-text-tertiary mt-1">
                          <div>{agentStats[agent.id]?.initialEmails || 0} initial</div>
                          <div>{agentStats[agent.id]?.fullEmails || 0} full details</div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm bg-status-info-bg">
                    {loadingStats ? (
                      <span className="text-text-tertiary">Loading...</span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="font-medium">{agentStats[agent.id]?.purchases || 0} leads</span>
                        {agentStats[agent.id]?.totalSpent > 0 && (
                          <span className="text-xs text-text-tertiary mt-1">
                            ${agentStats[agent.id]?.totalSpent.toFixed(2)} spent
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary">
                    {agent.notes ? (
                      <div className="max-w-md">
                        <p className="whitespace-pre-line">{agent.notes}</p>
                      </div>
                    ) : (
                      <span className="text-text-tertiary">No notes</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    <button
                      onClick={() => onEdit(agent)}
                      className="text-button-primary-bg hover:text-button-primary-hover mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(agent.id)}
                      className="text-status-error-text hover:text-status-error-border"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {statsError && (
            <div className="mt-2 p-2 bg-status-warning-bg text-status-warning-text text-sm rounded">
              {statsError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentList;